import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TableChart from './TableChart/TableChart';
import { useContainerReady, useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorTable from './Indicator/IndicatorTable';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import type { TopicExplainabilityExternalState } from './topicExplainability.types';
import { normalizeExplainabilityTitle } from './topicExplainability.utils';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  min-width: calc(50% - 5px);
  max-width: calc(50% - 5px);
  ${({ $iswide }) => $iswide === 1 && 'flex: 1;'}
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
`;

const ChartRow = styled.div`
  display: flex;
  width: 100%;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
  width: 100%;
`;

const FilterBarRow = styled.div`
  width: 100%;
  padding: 0 8px 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: var(--color-chart-fab);
  }
`;

const RelativeContainer = styled.div<{ $slotheight: number }>`
  position: relative;
  height: 100%;
  width: 100%;
  min-height: ${({ $slotheight }) => $slotheight}px;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-chart, #ffffff);
  z-index: 2;
`;

type TableChartPageProps = {
  navHirarchy: any;
  gridIndex?: number;
  gridHeight?: number;
  onGridClick?: (props: any) => void;
  gridFilterValues?: any;
  gridFilterValuesID?: any;
  gridPrism?: any;
  topicExplainabilityState?: TopicExplainabilityExternalState;
  onTopicExplainabilityStateChange?: (state: TopicExplainabilityExternalState) => void;
};

const DEFAULT_EXPLAINABILITY_STATE: TopicExplainabilityExternalState = {
  topic: '',
  phase: 'idle',
  loading: false,
  error: null,
  data: null,
  destinationTitle: null,
  sourceTitle: null,
  requestId: 0,
};

// eslint-disable-next-line @typescript-eslint/ban-types
function useHeatmapClick(navHirarchyItem: any, onScroll: Function, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          filterValues &&
          setIndicatorSections(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 400);
    },
    [navHirarchyItem, filterValues, onScroll],
  );

  return { indicatorSections, onClick };
}

export default function TableChartPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  onGridClick,
  gridFilterValues,
  gridFilterValuesID,
  gridPrism,
  topicExplainabilityState,
  onTopicExplainabilityStateChange,
}: TableChartPageProps) {
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  const explainabilityState = topicExplainabilityState ?? DEFAULT_EXPLAINABILITY_STATE;

  const tableTitle = navHirarchyItem?.heatmap?.title ?? navHirarchyItem?.name ?? '';
  const normalizedTableTitle = useMemo(() => normalizeExplainabilityTitle(tableTitle), [tableTitle]);
  const normalizedDestinationTitle = useMemo(
    () => normalizeExplainabilityTitle(explainabilityState.destinationTitle),
    [explainabilityState.destinationTitle],
  );
  const destinationCandidates = useMemo(() => {
    if (!normalizedDestinationTitle) return [] as string[];
    const aliases: Record<string, string[]> = {
      'media sources': ['narrative page explainability'],
      'narrative page explainability': ['media sources'],
    };
    return [normalizedDestinationTitle, ...(aliases[normalizedDestinationTitle] ?? [])];
  }, [normalizedDestinationTitle]);

  const isExplainabilityTarget = destinationCandidates.some(candidate =>
    candidate ? normalizedTableTitle.includes(candidate) : false,
  );
  useEffect(() => {
    if (!isExplainabilityTarget) return;
    if (explainabilityState.phase !== 'loading') return;
    onTopicExplainabilityStateChange?.({
      ...explainabilityState,
      phase: 'success',
      loading: false,
      error: null,
    });
  }, [explainabilityState, isExplainabilityTarget, onTopicExplainabilityStateChange]);

  const effectiveFilterValues = useMemo(
    () => (gridFilterValues != null ? gridFilterValues : filterValues),
    [gridFilterValues, filterValues],
  );

  const effectiveFilterValuesID = useMemo(
    () => (gridFilterValuesID != null ? gridFilterValuesID : filterValuesID),
    [gridFilterValuesID, filterValuesID],
  );

  const { data, error, isLoading, colorScheme, chartTitle, chartSubtitle, chartYtitle } = useHeatmapData(
    category,
    subcategory,
    subsubcategory,
    prism,
    effectiveFilterValuesID,
    prismId,
  );

  const resolvedChartTitle = useMemo(() => {
    if (chartTitle && chartTitle.trim().length > 0) {
      return chartTitle.trim();
    }
    const fallback = navHirarchyItem?.heatmap?.title ?? navHirarchyItem?.heatmap?.name ?? navHirarchyItem?.name ?? '';
    return (fallback || '').trim();
  }, [chartTitle, navHirarchyItem]);

  const { size, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const isContainerReady = useContainerReady(size, { minWidth: 40, minHeight: 0 });
  const safeGridHeight =
    typeof gridHeight === 'number' && Number.isFinite(gridHeight) && gridHeight > 0 ? gridHeight : 4;
  const slotHeightPx = Math.max((safeGridHeight / 4) * 454 + (safeGridHeight / 4 - 1) * 40, 160);

  const { indicatorSections, onClick } = useHeatmapClick(navHirarchyItem, scrollToBottom, effectiveFilterValues);

  const handleOnGridClick = useCallback(
    (props: any) => {
      if (!onGridClick) return;
      onGridClick({
        ...props,
        navHirarchyItem,
        filterValues: effectiveFilterValues,
        category,
        subcategory,
        subsubcategory,
        prism,
        prismId,
        indicatorSections,
      });
    },
    [
      navHirarchyItem,
      effectiveFilterValues,
      category,
      subcategory,
      subsubcategory,
      prism,
      prismId,
      indicatorSections,
      onGridClick,
    ],
  );

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      effectiveFilterValuesID,
      prismId,
      resolvedChartTitle,
    ).catch(e => {
      alert(e.message);
    });

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer id="tableChart">
        <ContentContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBarRow>
              <FilterBar
                type={navHirarchyItem?.type}
                filters={navHirarchyItem?.filters}
                values={effectiveFilterValues}
                filterHierarchy={navHirarchyItem?.narrativeFilterTree}
                filterHierarchyValues={effectiveFilterValuesID}
                onDownload={onDownload}
                tooltipCfg={tooltipCfg}
              />
            </FilterBarRow>
          )}
          <ChartRow>
            <HeatmapContainer ref={chartSizeRef}>
              <RelativeContainer $slotheight={slotHeightPx}>
                {(!isContainerReady || isLoading) && (
                  <LoaderOverlay>
                    <Loader />
                  </LoaderOverlay>
                )}
                {isContainerReady && !isLoading && data && (
                  <TableChart
                    key={resolvedChartTitle || 'table-chart'}
                    {...{
                      data,
                      headersData: JSON.parse(JSON.stringify(data)),
                      size,
                      onClick: gridIndex || gridIndex === 0 ? handleOnGridClick : onClick,
                      prism,
                      filterValues: effectiveFilterValues,
                      colorScheme,
                      chartTitle: resolvedChartTitle,
                      chartSubtitle,
                      chartYtitle,
                      tooltipCfg,
                      gridHeight,
                      gridPrism,
                    }}
                  />
                )}
              </RelativeContainer>
            </HeatmapContainer>
          </ChartRow>
        </ContentContainer>
      </MainContainer>
      <IndicatorsContainer>
        {!gridIndex &&
          gridIndex !== 0 &&
          indicatorSections.map((section: any, index: number) => (
            <IndicatorContainer
              key={'container' + section.indicator + section.topic + section.symbol + String(index)}
              $iswide={indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0 ? 1 : 0}
            >
              {section.type === 'bar' && (
                <IndicatorStackedAreaChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: effectiveFilterValues,
                    tooltipCfg,
                  }}
                ></IndicatorStackedAreaChart>
              )}
              {section.type === 'line' && (
                <IndicatorLineChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: effectiveFilterValues,
                    tooltipCfg,
                  }}
                ></IndicatorLineChart>
              )}
              {section.type === 'table' && (
                <IndicatorTable
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: effectiveFilterValues,
                    tooltipCfg,
                  }}
                ></IndicatorTable>
              )}
            </IndicatorContainer>
          ))}
      </IndicatorsContainer>
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
